import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysCompanyAccount } from '~/data/company-account/queryKeys';

import { queryKeysCompany } from '../queryKeys';

import { putCompanySupplierLetter } from './putCompanySupplierLetter';
import {
  type PutCompanySupplierLetterPayload,
  type SupplierLetterMeta,
} from './types';

/**
 * Custom hook for uploading or replacing the supplier authorization letter for a Company.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/put_company_supplier_letter_vadmin_companies__company_id__supplier_letter_put
 */
export const useMutationPutCompanySupplierLetter = (
  options?: Omit<
    UseMutationOptions<
      SupplierLetterMeta,
      Error,
      PutCompanySupplierLetterPayload
    >,
    'mutationFn' | 'onSuccess'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SupplierLetterMeta,
    Error,
    PutCompanySupplierLetterPayload
  >({
    async mutationFn({ companyId, body }) {
      return putCompanySupplierLetter(companyId, body);
    },
    async onSuccess(_data, { companyId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysCompany.getSupplierLetter(companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysCompanyAccount.getSupplierLetters(),
        }),
      ]);
    },
    ...options,
  });
};
